import * as m from '@staiter/i18n';
/**
 * FM-S-multi-4-17 — MembershipEditor (full page).
 *
 * Direct memberships table + Holding memberships table + add/remove.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, SkeletonTable, Table } from '~/components/data';
import { ModuleGrantsSection } from '~/components/domain/ModuleGrantsPanel';
import { emitToast } from '~/components/feedback';
import { Button, Dialog, Select } from '~/components/primitives';
import { StatusBadge } from '~/components/status';
import { setUserActive } from '~/lib/api/admin';
import {
  useAddEntityMembership,
  useAddHoldingMembership,
  useEntities,
  useHoldings,
  useRemoveEntityMembership,
  useRemoveHoldingMembership,
  useUpdateEntityMembership,
  useUpdateHoldingMembership,
  useUserMemberships,
} from '~/lib/api/tenancy';
import { ENTITY_MEMBERSHIP_ROLES, getRoleLabel } from '~/lib/rbac';
import { useIsSuperAdmin } from '~/lib/rbac/use-tenancy-rbac';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.admin.module.css';

// FM-S-multi-6-1: роли admin-уровня (для self-lockout — нельзя снять свой единственный
// административный доступ).
const ADMIN_ROLES = new Set(['admin', 'company_admin']);

export const Route = createFileRoute('/_auth/admin/users_/$userId/memberships')({
  component: UserMembershipsPage,
});

// Карта «ключ → подпись» живёт функцией, а не константой-словарём:
// локальный `Record<string, string>` — та же захардкоженная строка,
// только спрятанная в объект, и он же мешает подмене при смене локали.
function statusLabels(key: string): string {
  switch (key) {
    case 'active':
      return m.ui_aktiven();
    case 'invited':
      return m.ui_priglashen();
    case 'disabled':
      return m.ui_otklyuchen();
    default:
      return key;
  }
}
// Подписи ролей — единый источник правды (~/lib/rbac.getRoleLabel). Локальную карту
// убрали (рассинхрон названий, инцидент 2026-06-30). Опции выбора — ENTITY_MEMBERSHIP_ROLES.
const roleLabel = getRoleLabel;
const MEMBERSHIP_ROLE_OPTIONS = ENTITY_MEMBERSHIP_ROLES.map((r) => ({
  value: r,
  label: getRoleLabel(r),
}));
function statusLabel(status: string): string {
  return statusLabels(status) ?? status;
}

function UserMembershipsPage(): JSX.Element {
  const { userId } = Route.useParams();
  const mems = useUserMemberships(userId);
  const isSuper = useIsSuperAdmin();
  const [addEntityOpen, setAddEntityOpen] = useState(false);
  const [addHoldingOpen, setAddHoldingOpen] = useState(false);

  const removeM = useRemoveEntityMembership(userId);
  const removeHoldingM = useRemoveHoldingMembership(userId);
  const qc = useQueryClient();
  const actorUserId = useAuthStore((s) => s.user?.user_id ?? null);
  // F27 круг 2 (UX, MAJOR): у плашки отключения — действие, а не только слова.
  const enableMut = useMutation({
    mutationFn: () => setUserActive(userId, true),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'user-memberships', userId] });
      void mems.refetch();
    },
  });
  // FM-S-multi-6-1: role change через modal с обязательным reason.
  const [roleChange, setRoleChange] = useState<{ entityId: string; role: string } | null>(null);
  // Смена холдинг-роли — отдельный modal (BE не требует reason для холдинга).
  const [holdingRoleChange, setHoldingRoleChange] = useState<{
    holdingId: string;
    role: string;
  } | null>(null);

  // SR-F16 (F19 пака tails-closeout): голый «Загрузка…» не резервировал высоту
  // таблицы и нарушал §6.2 — скелетон строк держит сетку колонок.
  if (mems.isLoading) return <SkeletonTable rows={4} cols={5} />;
  const direct = mems.data?.direct ?? [];
  const holdingM = mems.data?.holding ?? [];
  const user = mems.data?.user ?? null;

  // FM-S-multi-6-1: self-lockout — если редактируем СВОЙ профиль и это единственный
  // admin-доступ, запрещаем снимать его (иначе lockout). BE тоже отдаёт 409, но upfront
  // disable + tooltip понятнее (per UX verdict).
  const editingSelf = !!actorUserId && actorUserId === userId;
  const adminDirectCount = direct.filter((m) => ADMIN_ROLES.has(m.role)).length;
  const isSelfLastAdmin = (role: string): boolean =>
    editingSelf && ADMIN_ROLES.has(role) && adminDirectCount <= 1;

  // FM-S-multi-6-1: единая таблица «Доступы» с колонкой «Источник» (вместо two-section
  // с сырыми UUID). direct → entity_name; holding → имя холдинга. RoleChangeModal +
  // self-lockout — остаются в FM-S-multi-6-1 как незакрытые подзадачи.
  type AccessRow =
    | {
        kind: 'entity';
        key: string;
        entityId: string;
        scope: string;
        source: 'direct';
        role: string;
        status: string;
      }
    | {
        kind: 'holding';
        key: string;
        holdingId: string;
        scope: string;
        source: 'holding';
        role: string;
        status: string;
      };

  const rows: AccessRow[] = [
    ...direct.map((m) => ({
      kind: 'entity' as const,
      key: `e:${m.entity_id}`,
      entityId: m.entity_id,
      scope: m.entity_name ?? '(без названия)',
      source: 'direct' as const,
      role: m.role,
      status: m.status,
    })),
    ...holdingM.map((m) => ({
      kind: 'holding' as const,
      key: `h:${m.holding_id}`,
      holdingId: m.holding_id,
      scope: `Холдинг «${m.holding_name ?? '—'}» · все компании`,
      source: 'holding' as const,
      role: m.role,
      status: m.status,
    })),
  ];

  return (
    <div>
      <PageHeader
        title={user ? `Доступы — ${user.full_name ?? user.email}` : 'Доступы пользователя'}
        description={user ? user.email : 'Прямые доступы + холдинг-уровень'}
      />
      {/* F27 (UX, круг 1): отключённая учётная запись — доступы правятся, но не
          действуют, пока её не включат; без плашки это было не видно. */}
      {user?.is_active === false && (
        <div
          className={[shared.inlineAlert, shared.inlineAlertWarn, styles.disabledBanner].join(' ')}
          role="status"
        >
          <AlertTriangle size={16} aria-hidden="true" />
          <span>{m.admin_memberships_user_disabled()}</span>
          {/* С-I2 Р.6 (В-48): весь аккаунт включает только администратор платформы — кнопка только у
              суперадмина, прочим — кто включает. */}
          {isSuper ? (
            <Button
              size="sm"
              variant="primary"
              disabled={enableMut.isPending}
              onClick={() => enableMut.mutate()}
            >
              {m.route_admin_users_restore_access()}
            </Button>
          ) : (
            <span className={styles.disabledWho}>{m.admin_memberships_enable_who()}</span>
          )}
        </div>
      )}

      <div style={{ marginTop: 16, marginBottom: 12, display: 'flex', gap: 8 }}>
        {/* F27 круг 3 (обе половины): у отключённой учётной записи основное действие —
            вернуть доступ; добавление компании — второстепенное. */}
        <Button
          variant={user?.is_active === false ? 'secondary' : 'primary'}
          onClick={() => setAddEntityOpen(true)}
          leadingIcon={<Plus size={14} />}
        >
          {m.ui_dobavit_dostup_k_kompanii()}
        </Button>
        {isSuper && (
          <Button
            variant="ghost"
            onClick={() => setAddHoldingOpen(true)}
            leadingIcon={<Plus size={14} />}
          >
            {m.ui_dobavit_dostup_k_holdingu()}
          </Button>
        )}
      </div>

      <Table
        ariaLabel="Доступы пользователя"
        data={rows}
        rowKey={(r) => r.key}
        columns={[
          { id: 'scope', header: 'Компания / холдинг', accessor: (r) => r.scope },
          {
            id: 'source',
            header: 'Источник',
            accessor: (r) => (
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: 'var(--radius-sm, 6px)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  background:
                    r.source === 'direct'
                      ? 'var(--surface-sunken, rgba(127,127,127,0.12))'
                      : 'var(--accent-bg-15, rgba(64,120,255,0.15))',
                  color:
                    r.source === 'direct'
                      ? 'var(--text-secondary, inherit)'
                      : 'var(--accent-strong, inherit)',
                }}
              >
                {r.source === 'direct' ? 'Прямой' : 'Холдинг'}
              </span>
            ),
          },
          { id: 'role', header: 'Роль', accessor: (r) => roleLabel(r.role) },
          {
            id: 'status',
            header: 'Статус',
            // F27 круг 2 (UX, MAJOR): при отключённой учётной записи строка «Активен»
            // выдавала нерабочее состояние за рабочее.
            accessor: (r) =>
              user?.is_active === false ? (
                <StatusBadge
                  status="terminated"
                  size="sm"
                  label={m.admin_memberships_not_in_effect()}
                />
              ) : (
                statusLabel(r.status)
              ),
          },
          {
            id: 'actions',
            header: '',
            accessor: (r) => {
              // Холдинг-доступ управляется только супер-админом (BE-gate тот же).
              if (r.kind === 'holding') {
                if (!isSuper) return null;
                return (
                  <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                    <Button
                      variant="ghost"
                      onClick={() => setHoldingRoleChange({ holdingId: r.holdingId, role: r.role })}
                      leadingIcon={<Pencil size={14} />}
                    >
                      {m.ui_izmenit_rol()}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={async () => {
                        if (!confirm(m.ui_udalit_dostup_k_holdingu_confirm())) return;
                        try {
                          await removeHoldingM.mutateAsync({ holdingId: r.holdingId });
                          emitToast({ variant: 'success', title: 'Удалено' });
                          await qc.invalidateQueries({
                            queryKey: ['admin', 'users', userId, 'memberships'],
                          });
                        } catch {
                          emitToast({ variant: 'error', title: 'Не удалось удалить' });
                        }
                      }}
                      leadingIcon={<Trash2 size={14} />}
                    >
                      {m.ui_udalit()}
                    </Button>
                  </div>
                );
              }
              const lockSelf = isSelfLastAdmin(r.role);
              return (
                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <Button
                    variant="ghost"
                    onClick={() => setRoleChange({ entityId: r.entityId, role: r.role })}
                    leadingIcon={<Pencil size={14} />}
                  >
                    {m.ui_izmenit_rol()}
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={lockSelf}
                    title={
                      lockSelf
                        ? 'Это ваш единственный административный доступ. Сначала добавьте другого администратора в эту компанию.'
                        : undefined
                    }
                    onClick={async () => {
                      if (!confirm(m.ui_udalit_dostup_confirm())) return;
                      try {
                        await removeM.mutateAsync({ entityId: r.entityId });
                        emitToast({ variant: 'success', title: 'Удалено' });
                        await qc.invalidateQueries({
                          queryKey: ['admin', 'users', userId, 'memberships'],
                        });
                      } catch {
                        emitToast({ variant: 'error', title: 'Не удалось удалить' });
                      }
                    }}
                    leadingIcon={<Trash2 size={14} />}
                  >
                    {m.ui_udalit()}
                  </Button>
                </div>
              );
            },
          },
        ]}
        emptyTitle="Доступов пока нет"
      />

      {/* access-control (F3) — конструктор модульного доступа: селектор компании + одна панель. */}
      {direct.length > 0 && (
        <ModuleGrantsSection
          userId={userId}
          entities={direct.map((mem) => ({
            entityId: mem.entity_id,
            entityName: mem.entity_name ?? '(без названия)',
          }))}
          currentUserId={actorUserId ?? undefined}
          accountDisabled={user?.is_active === false}
          // F3 — роль в каждой компании: панель обязана сказать, что у сервисной
          // роли пустой дефолт и без разрешающих строк доступа нет вовсе.
          rolesByEntity={Object.fromEntries(direct.map((mem) => [mem.entity_id, mem.role]))}
        />
      )}

      {roleChange && (
        <RoleChangeModal
          userId={userId}
          entityId={roleChange.entityId}
          currentRole={roleChange.role}
          onClose={() => setRoleChange(null)}
        />
      )}

      {holdingRoleChange && (
        <HoldingRoleChangeModal
          userId={userId}
          holdingId={holdingRoleChange.holdingId}
          currentRole={holdingRoleChange.role}
          onClose={() => setHoldingRoleChange(null)}
        />
      )}

      {addEntityOpen && (
        <AddEntityMembershipDialog userId={userId} onClose={() => setAddEntityOpen(false)} />
      )}
      {addHoldingOpen && (
        <AddHoldingMembershipDialog userId={userId} onClose={() => setAddHoldingOpen(false)} />
      )}
    </div>
  );
}

// FM-S-multi-6-1: RoleChangeModal — смена роли через диалог с обязательным reason
// (BE требует reason min 10 при смене роли — audit context, per UX verdict).
function RoleChangeModal({
  userId,
  entityId,
  currentRole,
  onClose,
}: {
  userId: string;
  entityId: string;
  currentRole: string;
  onClose: () => void;
}): JSX.Element {
  const [role, setRole] = useState(currentRole);
  const [reason, setReason] = useState('');
  const update = useUpdateEntityMembership(userId);
  const qc = useQueryClient();
  const reasonValid = reason.trim().length >= 10;
  const changed = role !== currentRole;

  return (
    <Dialog open onClose={onClose} title="Изменить роль">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!changed || !reasonValid) return;
          try {
            await update.mutateAsync({ entityId, input: { role, reason: reason.trim() } });
            emitToast({ variant: 'success', title: 'Роль изменена' });
            await qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'memberships'] });
            onClose();
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось изменить роль' });
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            label="Роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            // Если текущая роль вне курируемого набора (legacy) — добавляем её опцией,
            // чтобы значение отрисовалось, а не пропало.
            options={
              MEMBERSHIP_ROLE_OPTIONS.some((o) => o.value === currentRole)
                ? MEMBERSHIP_ROLE_OPTIONS
                : [
                    { value: currentRole, label: getRoleLabel(currentRole) },
                    ...MEMBERSHIP_ROLE_OPTIONS,
                  ]
            }
          />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>
              {m.ui_prichina_izmeneniya_dlya_zhurnala_audita()}
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={m.ui_minimum_10_simvolov_zachem_rol()}
              style={{
                padding: '8px 10px',
                border: '1px solid var(--border, #e5e7eb)',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: '0.9rem',
                resize: 'vertical',
              }}
            />
            {reason.length > 0 && !reasonValid && (
              <span style={{ fontSize: '0.75rem', color: 'var(--danger, #c00)' }}>
                {m.ui_minimum_10_simvolov()}
              </span>
            )}
          </label>
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {m.ui_otmena()}
          </Button>
          <Button type="submit" disabled={!changed || !reasonValid || update.isPending}>
            {m.ui_sohranit()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

// Смена холдинг-роли. Отдельный набор ролей (holding_admin / holding_viewer),
// reason не требуется (BE holding PATCH-схема без reason).
const HOLDING_ROLE_OPTIONS = [
  { value: 'holding_admin', label: 'Холдинг-админ' },
  { value: 'holding_viewer', label: 'Холдинг-наблюдатель' },
];

function HoldingRoleChangeModal({
  userId,
  holdingId,
  currentRole,
  onClose,
}: {
  userId: string;
  holdingId: string;
  currentRole: string;
  onClose: () => void;
}): JSX.Element {
  const [role, setRole] = useState(currentRole);
  const update = useUpdateHoldingMembership(userId);
  const qc = useQueryClient();
  const changed = role !== currentRole;

  return (
    <Dialog open onClose={onClose} title="Изменить роль в холдинге">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (!changed) return;
          try {
            await update.mutateAsync({ holdingId, input: { role } });
            emitToast({ variant: 'success', title: 'Роль изменена' });
            await qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'memberships'] });
            onClose();
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось изменить роль' });
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            label="Роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={
              HOLDING_ROLE_OPTIONS.some((o) => o.value === currentRole)
                ? HOLDING_ROLE_OPTIONS
                : [
                    { value: currentRole, label: getRoleLabel(currentRole) },
                    ...HOLDING_ROLE_OPTIONS,
                  ]
            }
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {m.ui_otmena()}
          </Button>
          <Button type="submit" disabled={!changed || update.isPending}>
            {m.ui_sohranit()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function AddEntityMembershipDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}): JSX.Element {
  const [entityId, setEntityId] = useState('');
  const [role, setRole] = useState('viewer');
  const entitiesQ = useEntities();
  const add = useAddEntityMembership(userId);
  const qc = useQueryClient();

  return (
    <Dialog open onClose={onClose} title={m.ui_dobavit_dostup_k_kompanii()}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await add.mutateAsync({ entity_id: entityId, role });
            emitToast({ variant: 'success', title: 'Доступ добавлен' });
            await qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'memberships'] });
            onClose();
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось добавить' });
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            label="Компания"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            required
            options={[
              { value: '', label: 'Выберите компанию' },
              ...(entitiesQ.data?.items ?? []).map((ent) => ({ value: ent.id, label: ent.name })),
            ]}
          />
          <Select
            label="Роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={MEMBERSHIP_ROLE_OPTIONS}
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {m.ui_otmena()}
          </Button>
          <Button type="submit" disabled={add.isPending}>
            {m.ui_dobavit()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function AddHoldingMembershipDialog({
  userId,
  onClose,
}: {
  userId: string;
  onClose: () => void;
}): JSX.Element {
  const [holdingId, setHoldingId] = useState('');
  const [role, setRole] = useState('holding_viewer');
  const holdingsQ = useHoldings();
  const add = useAddHoldingMembership(userId);
  const qc = useQueryClient();

  return (
    <Dialog open onClose={onClose} title={m.ui_dobavit_dostup_k_holdingu()}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await add.mutateAsync({ holding_id: holdingId, role });
            emitToast({ variant: 'success', title: 'Доступ к холдингу добавлен' });
            await qc.invalidateQueries({ queryKey: ['admin', 'users', userId, 'memberships'] });
            onClose();
          } catch {
            emitToast({ variant: 'error', title: 'Не удалось добавить' });
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Select
            label="Холдинг"
            value={holdingId}
            onChange={(e) => setHoldingId(e.target.value)}
            required
            options={[
              { value: '', label: 'Выберите холдинг' },
              ...(holdingsQ.data?.items ?? []).map((h) => ({ value: h.id, label: h.name })),
            ]}
          />
          <Select
            label="Роль"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            options={[
              { value: 'holding_admin', label: 'Холдинг-админ' },
              { value: 'holding_viewer', label: 'Холдинг-наблюдатель' },
            ]}
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {m.ui_otmena()}
          </Button>
          <Button type="submit" disabled={add.isPending}>
            {m.ui_dobavit()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
